import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  fetchCompanyBlogs,
} from "../redux/slices/blogSlice";

import {
  Search,
  CalendarDays,
  ArrowRight,
  Newspaper,
} from "lucide-react";

// ================= IMAGE PATH =================

// const newPath = "http://erp.metasolution.in/Uploads/";
const getImageUrl = (img) => {
  if (!img) return null;

  // already full URL hai to direct use
  if (img.startsWith("http")) return img;

  return `https://erpapi.sapeagleerp.com/uploads/${img
    .split(/[\\/]/)
    .pop()}`;
};

// ================= SLUG FUNCTION =================

const slugify = (text) =>
  text
    ?.toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/--+/g, "-");

// ================= COMPONENT =================

const AddBlog = () => {

  const dispatch = useDispatch();

  // ================= STATES =================

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredBlogs, setFilteredBlogs] = useState([]);

  // ================= REDUX =================

  const {
    blogs: companyBlogs,
    loading,
    error,
  } = useSelector((state) => state.blog);

  // ================= FETCH BLOGS =================

  useEffect(() => {
    dispatch(fetchCompanyBlogs(1));
  }, [dispatch]);

  // ================= FILTER =================

  useEffect(() => {

    if (!searchTerm.trim()) {

      setFilteredBlogs(companyBlogs || []);

    } else {

     const filtered = (companyBlogs || []).filter((blog) =>
          blog.title
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||

          blog.content
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||

          blog.categoryName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );

      setFilteredBlogs(filtered);
    }

  }, [searchTerm, companyBlogs]);

  return (

    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">

      {/* ================= HERO SECTION ================= */}

      <div className="relative overflow-hidden">

        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 opacity-95"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">

          <div className="text-center">

            <div className="flex justify-center mb-5">

              <div className="w-20 h-20 rounded-3xl bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center shadow-2xl">

                <Newspaper className="text-white" size={40} />

              </div>

            </div>

            <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight">

              Latest Blogs

            </h1>

            <p className="text-blue-100 text-lg mt-5 max-w-2xl mx-auto leading-8">

              Explore company updates, industry insights,
              trending technologies, and informative articles.

            </p>

            {/* SEARCH */}

            <div className="mt-10 flex justify-center">

              <div className="relative w-full max-w-2xl">

                <Search
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                  size={20}
                />

                <input
                  type="text"
                  placeholder="Search blogs, categories, topics..."
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(e.target.value)
                  }
                  className="w-full h-16 rounded-3xl bg-white/95 backdrop-blur-lg border border-white/20 pl-14 pr-5 text-gray-700 text-lg shadow-2xl outline-none focus:ring-4 focus:ring-white/30 transition-all"
                />

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ================= BLOG SECTION ================= */}

      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* LOADING */}

        {loading && (

          <div className="flex justify-center items-center py-28">

            <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>

          </div>

        )}

        {/* ERROR */}

        {error && (

          <div className="bg-red-50 border border-red-200 text-red-600 rounded-2xl p-5 text-center font-medium mb-8">

            {typeof error === "string"
              ? error
              : error?.message || "Something went wrong"}

          </div>

        )}

        {/* BLOG CARDS */}

        {!loading && filteredBlogs?.length > 0 && (

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

            {filteredBlogs.map((blog) => (

              <div
                key={blog.id}
                className="group bg-white rounded-[30px] overflow-hidden border border-gray-200/70 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >

                {/* IMAGE */}

                <div className="relative overflow-hidden">

                  {/* {blog.image1 ? (

                    <img
                      // src={`${newPath}${blog.image1}`}
                      src={getImageUrl(blog.image1)}
                      // alt={blog.title}
                      className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                    />

                  ) : ( */}

                    <div className="w-full h-64 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">

                      <Newspaper
                        className="text-white"
                        size={70}
                      />

                    </div>

                  {/* )} */}

                  {/* CATEGORY */}

                  <div className="absolute top-5 left-5">

                    <span className="px-4 py-2 rounded-full bg-white/90 backdrop-blur-lg text-xs font-bold text-indigo-600 shadow-md">

                      {blog.categoryName || "General"}

                    </span>

                  </div>

                </div>

                {/* CONTENT */}

                <div className="p-7">

                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">

                    <CalendarDays size={16} />

                    <span>
                      {blog.addedDate
                        ? blog.addedDate.split("T")[0]
                        : "N/A"}
                    </span>

                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-blue-600 transition-all">

                    {blog.title}

                  </h2>

                  <div
                    className="mt-5 text-gray-600 leading-7 text-sm line-clamp-4"
                    dangerouslySetInnerHTML={{
                      __html:
                        blog.content?.slice(0, 180) + "...",
                    }}
                  />

                  {/* BUTTON */}

                  <div className="mt-7">

                    <Link
                    to={`/blog/${blog.id}`}
                      className="h-11 inline-flex items-center px-5 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-semibold gap-2 shadow-lg hover:scale-105 transition-all duration-300"
                    >

                      View Details
                      <ArrowRight size={16} />

                    </Link>

                  </div>

                </div>

              </div>

            ))}

          </div>

        )}

        {/* NO BLOGS */}

        {!loading && filteredBlogs?.length === 0 && (

          <div className="text-center py-28">

            <div className="w-28 h-28 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-6">

              <Search className="text-gray-400" size={45} />

            </div>

            <h3 className="text-3xl font-bold text-gray-800">
              No Blogs Found
            </h3>

            <p className="text-gray-500 mt-3 text-lg">
              Try searching with another keyword.
            </p>

          </div>

        )}

      </div>

    </div>

  );
};

export default AddBlog;